import { useNavigate } from "@tanstack/react-router";
import {
  ControlCategories,
  getControlName,
  isDiscoveredDevicesControl,
  isEndpointManagementControl,
} from "@theprelude/core";
import { z } from "zod";
import { DefaultOverlay } from "@/components/default-overlay";
import { Filter, filtersSchema } from "@/lib/filter";
import { controlSchema } from "@/lib/validators";
import { AssetManagerEndpointsTable } from "../endpoint-tables/asset-manager-endpoints-table";
import { DiscoveredDevicesEndpointsTable } from "../endpoint-tables/discovered-devices-endpoints-table";

const overlay = "control-endpoints";

export const controlEndpointsSchema = z.tuple([
  z.literal(overlay),
  z.object({
    instanceId: z.string().optional(),
    control: controlSchema,
    category: z.nativeEnum(ControlCategories),
    filters: filtersSchema.optional().default([]),
  }),
]);

type ControlEndpointsSchema = z.infer<typeof controlEndpointsSchema>[1];

export const ControlEndpointsOverlay = ({
  instanceId,
  control,
  category,
  filters,
}: ControlEndpointsSchema) => {
  const navigate = useNavigate();

  const onFilter = (filters: Filter[]) =>
    navigate({
      to: window.location.pathname,
      params: (prev) => prev,
      search: (prev) => ({
        ...prev,
        overlay: [overlay, { instanceId, control, category, filters }],
      }),
    });

  if (isEndpointManagementControl(control) && instanceId !== undefined) {
    const title = `${getControlName(control)} Endpoints`;

    return (
      <DefaultOverlay title={title} defaultSize={60}>
        <div className="flex h-full flex-col gap-4 p-4 pb-0">
          <div>These endpoints were found in your asset manager.</div>
          <AssetManagerEndpointsTable
            instanceId={instanceId}
            control={control}
            filters={filters}
            onFilter={onFilter}
          />
        </div>
      </DefaultOverlay>
    );
  }

  if (isDiscoveredDevicesControl(control) && instanceId !== undefined) {
    const title = `${getControlName(control)} Endpoints`;

    return (
      <DefaultOverlay title={title} defaultSize={60}>
        <div className="flex h-full flex-col gap-4 p-4 pb-0">
          <div>These endpoints were found in your discoverable devices.</div>
          <DiscoveredDevicesEndpointsTable
            instanceId={instanceId}
            control={control}
            filters={filters}
            onFilter={onFilter}
          />
        </div>
      </DefaultOverlay>
    );
  }

  return null;
};
